import { MODULE_LABELS, normalizeResourcePerms } from "./iamPermissionLabels";

export const STUDIO_ACTIONS = [
  { key: "view", label: "View" },
  { key: "create", label: "Add" },
  { key: "edit", label: "Edit" },
  { key: "delete", label: "Delete" },
  { key: "approve", label: "Approve" },
  { key: "export", label: "Export" },
];

/** Modules that exist as working pages. Catalog keys outside this list stay hidden. */
const LIVE_MODULES = [
  "employees",
  "attendance",
  "leave",
  "payroll",
  "compliance",
  "settings",
];

const EXTRA_LABELS = {
  employees: "Employees",
  compliance: "Statutory & Compliance",
  settings: "Settings",
};

export function orderedModuleKeys(catalog) {
  const keys = Object.keys(catalog?.modules || {});
  return LIVE_MODULES.filter((key) => keys.includes(key));
}

/** Organisation owner is stored as admin and shown as Super Admin. */
export function displayRoleName(name) {
  const raw = String(name || "").trim();
  const key = raw.toLowerCase().replace(/[\s-]+/g, "_");
  if (key === "admin" || key === "super_admin" || key === "superadmin") return "Super Admin";
  return raw || "—";
}

export function moduleTitle(key) {
  return EXTRA_LABELS[key] || MODULE_LABELS[key] || key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export function moduleFlags(matrix, moduleKey, resources) {
  const bucket = matrix?.[moduleKey] || {};
  if (bucket._module && typeof bucket._module === "object") {
    const flags = {};
    STUDIO_ACTIONS.forEach(({ key }) => {
      flags[key] = !!(bucket._module[key] || bucket._module.full_control || bucket._module.manage);
    });
    return flags;
  }
  const rows = resources || Object.keys(bucket).filter((key) => !key.startsWith("_"));
  const flags = {};
  STUDIO_ACTIONS.forEach(({ key }) => {
    flags[key] = rows.some((res) => {
      const perms = normalizeResourcePerms(bucket[res]);
      return !!(perms[key] || perms.full_control || perms.manage);
    });
  });
  return flags;
}

export function applyModuleFlags(matrix, moduleKey, resources, flags) {
  const next = { ...(matrix || {}) };
  const bucket = { ...(next[moduleKey] || {}) };
  bucket._module = { ...flags };
  (resources || []).forEach((res) => {
    const current = normalizeResourcePerms(bucket[res]);
    const updated = { ...current };
    STUDIO_ACTIONS.forEach(({ key }) => {
      updated[key] = !!flags[key];
    });
    bucket[res] = updated;
  });
  next[moduleKey] = bucket;
  return next;
}

export function emptyModuleMatrix(catalog) {
  const matrix = {};
  orderedModuleKeys(catalog).forEach((key) => {
    matrix[key] = { _module: {} };
  });
  return matrix;
}

export function enabledPermissionCount(matrix, catalog) {
  let count = 0;
  orderedModuleKeys(catalog).forEach((key) => {
    const flags = moduleFlags(matrix, key, catalog.modules[key]);
    count += Object.values(flags).filter(Boolean).length;
  });
  return count;
}

export function permissionSummary(matrix, catalog) {
  if (!catalog) return "—";
  const modules = orderedModuleKeys(catalog);
  const enabledModules = modules.filter((key) =>
    Object.values(moduleFlags(matrix, key, catalog.modules[key])).some(Boolean)
  );
  const count = enabledPermissionCount(matrix, catalog);
  if (!count) return "No permissions";
  if (enabledModules.length === modules.length) return `${count} permissions · All modules`;
  return `${count} permissions · ${enabledModules.length} modules`;
}

export function initials(name) {
  const parts = String(name || "?").trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "?";
}

export function roleTone(name) {
  const n = String(name || "").toLowerCase();
  if (n.includes("admin")) return "admin";
  if (n.includes("payroll")) return "payroll";
  if (n.includes("hr")) return "hr";
  if (n.includes("manager") || n.includes("lead")) return "manager";
  return "employee";
}
